/**
 * Typed Supabase webhook event catalog.
 *
 * Supabase ships NO native signing on its Database Webhooks. Brew
 * mints a per-connection signing secret and the user pastes the
 * canonical SQL block (Vault entry + pgcrypto HMAC trigger function)
 * into their Supabase project. The receiver verifies the resulting
 * `X-Supabase-Signature` header.
 *
 * The catalog below covers the 12 events Brew exposes to Supabase
 * users:
 *
 *   - 3 raw events on `auth.users` (INSERT / UPDATE / DELETE).
 *   - 9 synthetic events derived from `record` vs `old_record`
 *     diffs on UPDATE (email confirmation, password change, etc).
 *
 * Hard schema invariant: every entry's payload schema includes
 * `{ key: 'email', type: 'string', required: true }`.
 */

import type {
  InboundEventCategory,
  InboundEventMetadata,
} from '@/lib/integrations/inbound/types'
import type {
  IntegrationTriggerField,
  IntegrationTriggerPayloadSchema,
} from '@/lib/triggers/types'

type TriggerField = IntegrationTriggerField
type TriggerPayloadSchema = IntegrationTriggerPayloadSchema
export type SupportedSupabaseEventType =
  | 'auth.user.created'
  | 'auth.user.updated'
  | 'auth.user.deleted'
  | 'auth.user.email_confirmed'
  | 'auth.user.phone_confirmed'
  | 'auth.user.email_changed'
  | 'auth.user.password_changed'
  | 'auth.user.signed_in'
  | 'auth.user.banned'
  | 'auth.user.unbanned'
  | 'auth.user.invited'
  | 'auth.user.upgraded_from_anonymous'

export const SUPPORTED_SUPABASE_EVENTS: ReadonlyArray<SupportedSupabaseEventType> =
  [
    'auth.user.created',
    'auth.user.updated',
    'auth.user.deleted',
    'auth.user.email_confirmed',
    'auth.user.phone_confirmed',
    'auth.user.email_changed',
    'auth.user.password_changed',
    'auth.user.signed_in',
    'auth.user.banned',
    'auth.user.unbanned',
    'auth.user.invited',
    'auth.user.upgraded_from_anonymous',
  ] as const

const SUPPORTED_SUPABASE_EVENT_SET = new Set<string>(SUPPORTED_SUPABASE_EVENTS)

/**
 * Slim user-facing common preamble. Supabase event metadata
 * (`supabaseEventId`, `supabaseEventType`, `supabaseTable`,
 * `supabaseSchema`, `userId`) is intentionally absent — the extractor
 * still emits those keys into `triggerInstances.payload` for the
 * contact mapper to read.
 */
const COMMON_FIELDS: Array<TriggerField> = [
  {
    key: 'email',
    type: 'string',
    required: true,
    description:
      "Recipient email derived from the Supabase auth.users record. Falls back to `<userId>@<orgId>.brew.local` when the user's email column is unset.",
    exampleValue: 'alex@example.com',
  },
  {
    key: 'phone',
    type: 'string',
    required: false,
    description: 'User phone number when present.',
    exampleValue: '+15551234567',
  },
  {
    key: 'role',
    type: 'string',
    required: false,
    description: 'Supabase user role when set (e.g. `authenticated`).',
    exampleValue: 'authenticated',
  },
  {
    key: 'createdAt',
    type: 'int',
    required: false,
    description:
      'Unix timestamp (seconds) derived from `record.created_at` when available.',
    exampleValue: 1_710_000_000,
  },
  {
    key: 'lastSignInAt',
    type: 'int',
    required: false,
    description:
      'Unix timestamp (seconds) of the latest known sign-in (`record.last_sign_in_at`). Always reflects the current state on the row — distinct from the synthetic `signed_in` event which carries the transition moment in `newValue`.',
    exampleValue: 1_710_000_500,
  },
  {
    key: 'emailConfirmedAt',
    type: 'int',
    required: false,
    description:
      'Unix timestamp (seconds) of when the user confirmed their email (`record.email_confirmed_at`). Mirrors the current row state — the synthetic `email_confirmed` event carries the transition moment in `newValue`.',
    exampleValue: 1_710_000_100,
  },
  {
    key: 'firstName',
    type: 'string',
    required: false,
    description:
      'First name from `raw_user_meta_data.first_name` (email signup) or `raw_user_meta_data.given_name` (OAuth signup).',
    exampleValue: 'Alex',
  },
  {
    key: 'lastName',
    type: 'string',
    required: false,
    description:
      'Last name from `raw_user_meta_data.last_name` (email signup) or `raw_user_meta_data.family_name` (OAuth signup).',
    exampleValue: 'Smith',
  },
  {
    key: 'fullName',
    type: 'string',
    required: false,
    description:
      'Full name from `raw_user_meta_data.full_name` (email signup) or `raw_user_meta_data.name` (OAuth signup).',
    exampleValue: 'Alex Smith',
  },
  {
    key: 'avatarUrl',
    type: 'string',
    required: false,
    description:
      'Avatar URL from `raw_user_meta_data.avatar_url` (email signup) or `raw_user_meta_data.picture` (OAuth signup).',
    exampleValue: 'https://example.com/avatar.png',
  },
  {
    key: 'displayName',
    type: 'string',
    required: false,
    description:
      'Display name from `raw_user_meta_data.display_name` when present.',
    exampleValue: 'Alex S.',
  },
  {
    key: 'provider',
    type: 'string',
    required: false,
    description:
      'Signup method from `raw_app_meta_data.provider` — e.g. `email`, `google`, `github`. Useful for routing different welcome flows per signup source.',
    exampleValue: 'email',
  },
]

const RAW_EXTRA_FIELDS: Array<TriggerField> = [
  // `rawType` is constant per event type (created=INSERT,
  // updated=UPDATE, deleted=DELETE) so it carries zero filter/split
  // value — internal-only. The extractor still emits it for debugging.
  {
    key: 'rawType',
    type: 'string',
    required: false,
    description:
      'Raw Supabase event type (`INSERT` / `UPDATE` / `DELETE`) before Brew classification.',
    exampleValue: 'INSERT',
    internal: true,
  },
]

const DELETED_EXTRA_FIELDS: Array<TriggerField> = [...RAW_EXTRA_FIELDS]

const SYNTHETIC_PAIR_FIELDS: Array<TriggerField> = [
  {
    key: 'previousValue',
    type: 'string',
    required: false,
    description:
      'Previous column value (when the synthetic event was triggered by a diff).',
    // Must be NON-EMPTY: `validateAndResolvePayload` treats `''` as
    // MISSING and drops the key, so an empty example documents a value
    // the pipeline structurally cannot deliver.
    exampleValue: 'old@example.com',
  },
  {
    key: 'newValue',
    type: 'string',
    required: false,
    description:
      'New column value (when the synthetic event was triggered by a diff).',
    exampleValue: 'alex@example.com',
  },
]

// One-sided variants: a null→set transition (confirmations, ban, invite) only
// has a NEW value; a set→null transition (unban) only has a PREVIOUS value; and
// `password_changed` has no templatable value at all. Declaring the FULL pair on
// those events left the empty half as a DEAD picker field (offered but always
// empty at runtime), so each event advertises only the half its
// `classifySupabaseUpdate` branch actually populates.
const NEW_VALUE_ONLY_FIELDS: Array<TriggerField> = SYNTHETIC_PAIR_FIELDS.filter(
  (field) => field.key === 'newValue'
)
const PREVIOUS_VALUE_ONLY_FIELDS: Array<TriggerField> =
  SYNTHETIC_PAIR_FIELDS.filter((field) => field.key === 'previousValue')

function buildPayloadSchema(
  extras: ReadonlyArray<TriggerField>
): TriggerPayloadSchema {
  return {
    type: 'object',
    fields: [...COMMON_FIELDS, ...extras],
  }
}

function buildSamplePayload(
  _eventType: SupportedSupabaseEventType,
  payload: Record<string, string | number | boolean | null>
): Record<string, string | number | boolean | null> {
  return {
    email: 'alex@example.com',
    firstName: 'Alex',
    lastName: 'Smith',
    ...payload,
  }
}

export const SUPABASE_EVENT_CATEGORIES: ReadonlyArray<InboundEventCategory> = [
  { id: 'user', label: 'User' },
]

export const SUPABASE_EVENT_METADATA: Array<InboundEventMetadata> = [
  {
    eventType: 'auth.user.created',
    title: 'Supabase - User Created',
    description:
      'Fires on the raw INSERT in `auth.users`. Use to send a welcome email or sync the contact into Brew.',
    category: 'user',
    payloadSchema: buildPayloadSchema(RAW_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('auth.user.created', {
      role: 'authenticated',
      createdAt: 1_710_000_000,
      firstName: 'Alex',
      lastName: 'Smith',
      fullName: 'Alex Smith',
      avatarUrl: 'https://example.com/avatar.png',
      provider: 'email',
    }),
    useCases: [
      {
        title: 'Welcome email',
        description: 'Greet brand-new signups with onboarding next steps.',
      },
      {
        title: 'Contact sync',
        description: 'Add the new user to Brew contacts for segmentation.',
      },
    ],
  },
  {
    eventType: 'auth.user.updated',
    title: 'Supabase - User Updated',
    description:
      'Fires on a raw UPDATE in `auth.users` that did not match a more specific synthetic event. Useful for generic profile-change flows.',
    category: 'user',
    payloadSchema: buildPayloadSchema(RAW_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('auth.user.updated', {
      role: 'authenticated',
    }),
    useCases: [
      {
        title: 'Profile-change confirmation',
        description: 'Confirm broad account-record changes for the user.',
      },
    ],
  },
  {
    eventType: 'auth.user.deleted',
    title: 'Supabase - User Deleted',
    description:
      'Fires on the raw DELETE in `auth.users`. Use to send a farewell email or remove the matching Brew contact.',
    category: 'user',
    payloadSchema: buildPayloadSchema(DELETED_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('auth.user.deleted', {}),
    useCases: [
      {
        title: 'Offboarding email',
        description: 'Send a graceful goodbye + reactivation path.',
      },
    ],
  },
  {
    eventType: 'auth.user.email_confirmed',
    title: 'Supabase - Email Confirmed',
    description:
      'Synthetic: derived when `email_confirmed_at` transitions from null → non-null. Fires the moment a user verifies their email.',
    category: 'user',
    payloadSchema: buildPayloadSchema(NEW_VALUE_ONLY_FIELDS),
    samplePayload: buildSamplePayload('auth.user.email_confirmed', {
      newValue: '2024-03-09T12:00:00Z',
    }),
    useCases: [
      {
        title: 'Post-verification welcome',
        description:
          'Trigger the real welcome flow once the email is verified.',
      },
    ],
  },
  {
    eventType: 'auth.user.phone_confirmed',
    title: 'Supabase - Phone Confirmed',
    description:
      'Synthetic: derived when `phone_confirmed_at` transitions from null → non-null.',
    category: 'user',
    payloadSchema: buildPayloadSchema(NEW_VALUE_ONLY_FIELDS),
    samplePayload: buildSamplePayload('auth.user.phone_confirmed', {
      newValue: '2024-03-09T12:05:00Z',
    }),
    useCases: [
      {
        title: 'SMS-confirmation follow-up',
        description: 'Send post-verification messaging once SMS is verified.',
      },
    ],
  },
  {
    eventType: 'auth.user.email_changed',
    title: 'Supabase - Email Changed',
    description:
      'Synthetic: derived when the `email` column changes between rows.',
    category: 'user',
    payloadSchema: buildPayloadSchema(SYNTHETIC_PAIR_FIELDS),
    samplePayload: buildSamplePayload('auth.user.email_changed', {
      previousValue: 'old@example.com',
      newValue: 'alex@example.com',
    }),
    useCases: [
      {
        title: 'Security alert to old email',
        description:
          'Notify the previous email address that the account email changed.',
      },
    ],
  },
  {
    eventType: 'auth.user.password_changed',
    title: 'Supabase - Password Changed',
    description:
      'Synthetic: derived when `encrypted_password` changes between rows.',
    category: 'user',
    // No templatable value diff — and surfacing old/new password data would be
    // undesirable anyway — so this event declares NO value fields.
    payloadSchema: buildPayloadSchema([]),
    samplePayload: buildSamplePayload('auth.user.password_changed', {}),
    useCases: [
      {
        title: 'Security alert',
        description: 'Confirm the password change with a "this was me" CTA.',
      },
    ],
  },
  {
    eventType: 'auth.user.signed_in',
    title: 'Supabase - Signed In',
    description:
      'Synthetic: derived when `last_sign_in_at` changes. Note: Supabase token refreshes also bump this column — protect against refresh storms with a downstream filter.',
    category: 'user',
    payloadSchema: buildPayloadSchema(SYNTHETIC_PAIR_FIELDS),
    samplePayload: buildSamplePayload('auth.user.signed_in', {
      newValue: '2024-03-09T12:10:00Z',
    }),
    useCases: [
      {
        title: 'Re-engagement detection',
        description:
          'Detect dormant users returning — but be careful of refresh storms (token rotations bump this column).',
      },
    ],
  },
  {
    eventType: 'auth.user.banned',
    title: 'Supabase - User Banned',
    description:
      'Synthetic: derived when `banned_until` transitions from null → non-null.',
    category: 'user',
    payloadSchema: buildPayloadSchema(NEW_VALUE_ONLY_FIELDS),
    samplePayload: buildSamplePayload('auth.user.banned', {
      newValue: '2099-01-01T00:00:00Z',
    }),
    useCases: [
      {
        title: 'Account-suspension notice',
        description: 'Notify the user (and ops) that the account is suspended.',
      },
    ],
  },
  {
    eventType: 'auth.user.unbanned',
    title: 'Supabase - User Unbanned',
    description:
      'Synthetic: derived when `banned_until` transitions from non-null → null.',
    category: 'user',
    payloadSchema: buildPayloadSchema(PREVIOUS_VALUE_ONLY_FIELDS),
    samplePayload: buildSamplePayload('auth.user.unbanned', {
      previousValue: '2099-01-01T00:00:00Z',
    }),
    useCases: [
      {
        title: 'Reinstated-account notice',
        description: 'Welcome the user back after a successful appeal.',
      },
    ],
  },
  {
    eventType: 'auth.user.invited',
    title: 'Supabase - User Invited',
    description:
      'Synthetic: derived when `invited_at` transitions from null → non-null.',
    category: 'user',
    payloadSchema: buildPayloadSchema(NEW_VALUE_ONLY_FIELDS),
    samplePayload: buildSamplePayload('auth.user.invited', {
      newValue: '2024-03-09T12:00:00Z',
    }),
    useCases: [
      {
        title: 'Pending-invite reminder',
        description:
          'Drip a "still waiting for you" reminder to the invitee until they accept.',
      },
    ],
  },
  {
    eventType: 'auth.user.upgraded_from_anonymous',
    title: 'Supabase - Upgraded From Anonymous',
    description:
      'Synthetic: derived when `is_anonymous` transitions from true → false. Useful for apps using Supabase anon auth.',
    category: 'user',
    payloadSchema: buildPayloadSchema(SYNTHETIC_PAIR_FIELDS),
    samplePayload: buildSamplePayload('auth.user.upgraded_from_anonymous', {
      previousValue: 'true',
      newValue: 'false',
    }),
    useCases: [
      {
        title: 'Post-signup welcome',
        description:
          'Send the welcome email when an anonymous user converts to a real account.',
      },
    ],
  },
]

export function isSupportedSupabaseEvent(
  eventType: string
): eventType is SupportedSupabaseEventType {
  return SUPPORTED_SUPABASE_EVENT_SET.has(eventType)
}
